import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useSession } from '../../auth';
import { Link } from './Link';
import { phoneHintArmed } from '../../../../../shared-web/phone-hint';

/**
 * The signed-in account affordance, shared by the shell TopNav and the
 * landing's own nav: the caller supplies the trigger's look (class + glyph),
 * this owns the flow — the trigger opens one menu holding the person's name,
 * Settings, Feedback, and Log out. Chats and the notebook are not in it
 * (2026-10-21 founder, as in the apps): they are doors of their own beside the
 * circle (NavAccountButton).
 */
export function AccountMenu({ buttonClassName, children }: {
  buttonClassName: string;
  children: ReactNode;
}): ReactElement {
  const { session, logout } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  // Close the account menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: PointerEvent) => {
      if (!accountRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleLogout = async (): Promise<void> => {
    setMenuOpen(false);
    try { await logout(); } catch { /* server revoke is best-effort */ }
    window.location.href = '/';
  };

  return (
    <div className="ember-nav-account" ref={accountRef}>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label="Account menu"
        data-log="nav_account_menu"
        // The action this tap performs: one name used to cover open AND
        // close, so every count read roughly double (2026-08-31 audit).
        data-log-state={menuOpen ? 'close' : 'open'}
      >
        {children}
      </button>
      {menuOpen ? (
        <div className="ember-nav-menu" role="menu">
          {session?.self_name ? <div className="ember-nav-menu-name">{session.self_name}</div> : null}
          {/* Delete account moved off this menu (2026-08-23): it lives
              on /settings with the data-use switches. */}
          <Link
            to="/settings"
            className="ember-nav-menu-item"
            role="menuitem"
            data-log="nav_settings"
            onClick={() => setMenuOpen(false)}
          >
            <span>Settings</span>
            {/* Google 首登后的一次性「绑个手机号」引导(2026-09-09):Settings
                看过一眼(phone-hint.ts markPhoneHintSeen)就熄灭。 */}
            {phoneHintArmed() ? <span className="ember-nav-menu-dot" aria-hidden /> : null}
          </Link>
          {/* The message board to the team — the signed-in copy of the
              footer's Feedback entry. */}
          <Link
            to="/feedback"
            className="ember-nav-menu-item"
            role="menuitem"
            data-log="nav_feedback"
            onClick={() => setMenuOpen(false)}
          >
            Feedback
          </Link>
          <button type="button" className="ember-nav-menu-item" role="menuitem" data-log="nav_logout" onClick={handleLogout}>
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}
